import { v4 as uuid } from 'uuid';
import type { StudioBinding, StudioDeck, StudioField, StudioLineEntry, StudioLineSet } from 'src/components/star-grid/reportStudio.types';
import type { PnlHierarchyMember, PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { expandableMembersOf } from 'src/components/star-grid/utils/lineSetExpansion';

/** Lines seeded from a tree: its roots and their children, the view a deck row set usually starts from. */
const SEED_DEPTH = 1;

const treeEntries = (members: readonly PnlHierarchyMember[], bindingId: string, depth = 0): StudioLineEntry[] =>
  members.flatMap((member) => [
    {
      id: uuid(),
      kind: 'member' as const,
      label: member.label || member.id,
      member_id: member.id,
      binding_id: bindingId,
      ...(depth ? { indent: depth } : { emphasis: 'bold' as const })
    },
    ...(depth < SEED_DEPTH ? treeEntries(member.children ?? [], bindingId, depth + 1) : [])
  ]);

/**
 * The ONE line set the deck's rows share, or `undefined` when no row points at one yet.
 *
 * The report renders a single statement, and only the set the rows shelf points at is read back into
 * the spec -- so a second row dimension has to extend that set rather than register its own, which
 * showed as extra blocks in the editor and contributed no rows to the report.
 */
export const deckRowsLineSet = (deck: StudioDeck, exceptFieldId?: string): StudioLineSet | undefined =>
  deck.line_sets.find((set) => deck.rows.some((row) => row.id !== exceptFieldId && row.selection.reference_id === set.id));

/** `entries` appended to the deck's rows set, or a new set when there is none, with the field pointed at it. */
const rowsSetWith = (
  field: StudioField,
  binding: StudioBinding,
  entries: readonly StudioLineEntry[],
  existing?: StudioLineSet
): { readonly set: StudioLineSet; readonly field: StudioField } => {
  // New nodes show no expand icon in the report until asked; a set still deriving its icons keeps the ones it shows.
  const set: StudioLineSet = existing
    ? { ...existing, entries: [...existing.entries, ...entries], expandable_members: expandableMembersOf(existing) }
    : { id: `lines-${field.id}`, label: binding.label, version: 'studio', binding_id: binding.id, editable: true, entries, expandable_members: [] };
  return { set, field: { ...field, selection: { kind: 'line_set', reference_id: set.id, members: [] } } };
};

/**
 * A line set for a rows field that has none, and the field pointed at it.
 *
 * A dimension dropped on Rows starts as "all members", so its pencil had no line set to open and fell
 * back to the member modal. Seeded from the members already picked, else from the hierarchy's top.
 */
export const lineSetForField = (
  field: StudioField,
  binding: StudioBinding,
  tree?: PnlHierarchyTree,
  existing?: StudioLineSet
): { readonly set: StudioLineSet; readonly field: StudioField } => {
  const picked = field.selection.members.map(
    (member): StudioLineEntry => ({ id: uuid(), kind: 'member', label: member, member_id: member, binding_id: binding.id })
  );
  return rowsSetWith(field, binding, picked.length ? picked : treeEntries(tree?.members ?? [], binding.id), existing);
};

const positions = (members: readonly PnlHierarchyMember[], depth = 0): { member: PnlHierarchyMember; depth: number }[] =>
  members.flatMap((member) => [{ member, depth }, ...positions(member.children ?? [], depth + 1)]);

/** Picks in display order: each pick with no picked ancestor, in the order ticked, followed by its picked descendants in hierarchy order. */
const orderedPicks = (picked: readonly string[], members: readonly PnlHierarchyMember[]): string[] => {
  const chosen = new Set(picked);
  const parents = new Map<string, string | undefined>();
  const nodes = new Map<string, PnlHierarchyMember>();
  const index = (node: PnlHierarchyMember, parent?: string): void => {
    if (nodes.has(node.id)) return;
    nodes.set(node.id, node);
    parents.set(node.id, parent);
    node.children?.forEach((child) => index(child, node.id));
  };
  members.forEach((member) => index(member));
  const underPick = (id: string): boolean => {
    for (let ancestor = parents.get(id); ancestor; ancestor = parents.get(ancestor)) if (chosen.has(ancestor)) return true;
    return false;
  };
  const subtree = (node: PnlHierarchyMember): string[] =>
    (node.children ?? []).flatMap((child) => [...(chosen.has(child.id) ? [child.id] : []), ...subtree(child)]);
  const roots = picked.filter((id) => !underPick(id));
  return [...new Set(roots.flatMap((id) => [id, ...(nodes.has(id) ? subtree(nodes.get(id)!) : [])]))];
};

/**
 * The members picked in the add-row modal appended to the deck's rows set. Ticking a parent ticks its subtree, so each
 * picked subtree is laid out in hierarchy order, the subtrees in the order they were ticked. Each line is indented by its
 * hierarchy depth relative to the shallowest pick, which is bold -- the shape the report renders. A member absent from
 * the tree is a top-level line. Each line carries its OWN dimension, so its pencil opens that dimension's hierarchy
 * rather than the set's.
 */
export const pickedLineSet = (
  field: StudioField,
  binding: StudioBinding,
  picked: readonly string[],
  tree?: PnlHierarchyTree,
  existing?: StudioLineSet
): { readonly set: StudioLineSet; readonly field: StudioField } => {
  const byId = new Map<string, { member: PnlHierarchyMember; depth: number }>();
  for (const position of positions(tree?.members ?? [])) if (!byId.has(position.member.id)) byId.set(position.member.id, position);
  const unique = orderedPicks(picked, tree?.members ?? []);
  const depths = unique.map((id) => byId.get(id)?.depth).filter((depth): depth is number => depth !== undefined);
  const top = depths.length ? Math.min(...depths) : 0;
  const entries = unique.map((id): StudioLineEntry => {
    const found = byId.get(id);
    const depth = found ? found.depth - top : 0;
    return {
      id: uuid(),
      kind: 'member',
      label: found?.member.label || id,
      member_id: id,
      binding_id: binding.id,
      ...(depth > 0 ? { indent: depth } : { emphasis: 'bold' as const })
    };
  });
  return rowsSetWith(field, binding, entries, existing);
};
